import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { useTheme } from '../theme/ThemeContext'
import { ChromeTooltip } from './ChromeTooltip'
import { IconAppendGenerate, IconDelete, IconMoreVertical, IconSaveLibrary } from './timeline-action-icons'

export type RoundQuickAction = {
  key: string
  /** 悬浮提示 */
  tooltip: string
  /** tooltip 对齐 */
  tooltipSide?: 'top' | 'bottom'
  /** 禁用原因 */
  disabledReason?: string
  disabled?: boolean
  danger?: boolean
  onSelect?: () => void
}

type Props = {
  /** 三按钮顺序：追加生成 / 存入图库 / 删除（SUCCEEDED）或 重试落库 / 重新生成 / 删除（FAILED） */
  actions: RoundQuickAction[]
  angular?: boolean
  ariaLabel?: string
}

const VIEWPORT_MARGIN = 8
const GAP = 4
/** 每个图标按钮 28px + 间距 8px，估算 popover 宽 */
const EST_BTN_W = 28
const BTN_GAP = 8

/**
 * 时间线轮次快捷操作：
 * 三圆点 trigger → 弹出水平排列的图标按钮条，每个按钮悬浮用 ChromeTooltip 显示说明。
 * Popover 层级与 SelectMenu 一致，打开时任何滚动关闭。
 */
export function ChromeRoundActionMenu({
  actions,
  angular: angularProp,
  ariaLabel = '轮次快捷操作',
}: Props) {
  const { theme } = useTheme()
  const angular = angularProp !== undefined ? angularProp : theme === 'pixel' || theme === 'geek'
  const [open, setOpen] = useState(false)
  const [layout, setLayout] = useState<{ top: number; left: number } | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const reducedMotion = useReducedMotion()

  const computeLayout = useCallback(() => {
    const el = triggerRef.current
    if (!el) return null
    const rect = el.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    const count = Math.min(actions.length, 4)
    const popW = count * EST_BTN_W + (count - 1) * BTN_GAP + 16 // 16px padding
    const popH = 36 // 按钮高 + padding
    const spaceBelow = vh - rect.bottom - GAP
    const spaceAbove = rect.top - GAP
    const placeBelow = spaceBelow >= popH || spaceBelow >= spaceAbove * 0.85
    const top = placeBelow
      ? rect.bottom + GAP
      : Math.max(VIEWPORT_MARGIN, rect.top - GAP - popH)
    // 水平方向：以 trigger 右边缘为锚点向右展开；溢出则左对齐到 trigger
    let left = rect.right - popW
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN
    if (left + popW > vw - VIEWPORT_MARGIN) left = Math.max(VIEWPORT_MARGIN, vw - VIEWPORT_MARGIN - popW)
    return { top, left }
  }, [actions.length])

  const handleToggle = useCallback(() => {
    if (open) {
      setOpen(false)
      return
    }
    const pos = computeLayout()
    if (!pos) return
    setLayout(pos)
    setOpen(true)
  }, [open, computeLayout])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node
      if (popoverRef.current?.contains(t) || triggerRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    const onScroll = () => setOpen(false)
    const onResize = () => {
      const pos = computeLayout()
      if (pos) setLayout(pos)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    document.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
  }, [open, computeLayout])

  const handleSelect = useCallback((action: RoundQuickAction) => {
    if (action.disabled || !action.onSelect) return
    action.onSelect()
    setOpen(false)
  }, [])

  const frameClass = [
    'fixed z-[120000]',
    'border border-line bg-[var(--menu-solid)] shadow-[0_8px_24px_-8px_rgba(0,0,0,0.18)]',
    angular ? 'rounded-none' : 'rounded-[var(--radius-md)]',
  ].join(' ')

  const iconBtnClass = [
    'inline-flex shrink-0 items-center justify-center',
    'h-7 w-7',
    angular ? 'rounded-none' : 'rounded-[var(--radius-sm)]',
    'border border-line bg-[var(--menu-solid)] text-ink/72',
    'hover:border-accent/70 hover:text-ink hover:bg-surface-muted',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
    'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-line disabled:hover:bg-[var(--menu-solid)] disabled:hover:text-ink/40',
    'transition-colors duration-120',
  ].join(' ')

  const iconBtnDangerClass = [
    'inline-flex shrink-0 items-center justify-center',
    'h-7 w-7',
    angular ? 'rounded-none' : 'rounded-[var(--radius-sm)]',
    'border border-[color-mix(in_srgb,var(--color-error)_48%,var(--line)_52%)] bg-[var(--menu-solid)] text-[rgb(225_110_125)]',
    'hover:border-[color-mix(in_srgb,var(--color-error)_72%,var(--line)_28%)] hover:text-[rgb(225_110_125)] hover:bg-[color-mix(in_srgb,var(--menu-solid)_90%,rgb(248_165_174)_10%)]',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(225_110_125)]/60',
    'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-40',
    'transition-colors duration-120',
  ].join(' ')

  function renderIcon(key: string) {
    const cls = 'h-4 w-4'
    switch (key) {
      case 'append': return <IconAppendGenerate className={cls} angular={angular} />
      case 'save': return <IconSaveLibrary className={cls} angular={angular} />
      case 'delete': return <IconDelete className={cls} angular={angular} />
      default: return null
    }
  }

  const portalContent = (
    <AnimatePresence>
      {open && layout ? (
        <motion.div
          ref={popoverRef}
          role="toolbar"
          aria-label={ariaLabel}
          initial={reducedMotion ? false : { opacity: 0, y: -4, scale: 0.97 }}
          animate={reducedMotion ? undefined : { opacity: 1, y: 0, scale: 1 }}
          exit={reducedMotion ? undefined : { opacity: 0, y: -4, scale: 0.97 }}
          transition={{ duration: 0.12, ease: 'easeOut' as const }}
          className={frameClass}
          style={{ top: layout.top, left: layout.left }}
        >
          <div className="flex items-center gap-2 px-2 py-1.5">
            {actions.map((a) => (
              <ChromeTooltip
                key={a.key}
                content={a.tooltip}
                angular={angular}
                side={a.tooltipSide ?? 'top'}
                align="center"
              >
                <button
                  type="button"
                  aria-label={a.tooltip}
                  disabled={a.disabled}
                  title={a.disabledReason}
                  className={a.danger ? iconBtnDangerClass : iconBtnClass}
                  onClick={() => handleSelect(a)}
                >
                  {renderIcon(a.key)}
                </button>
              </ChromeTooltip>
            ))}
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  )

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={handleToggle}
        className={[
          'inline-flex shrink-0 items-center justify-center',
          'h-7 w-7',
          angular ? 'rounded-none' : 'rounded-[var(--radius-sm)]',
          'border border-line bg-panel text-ink/72',
          'hover:border-accent/70 hover:text-ink hover:bg-surface-muted',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
          'transition-colors duration-120',
        ].join(' ')}
      >
        <IconMoreVertical className="h-4 w-4" angular={angular} />
      </button>
      {typeof document !== 'undefined' ? createPortal(portalContent, document.body) : null}
    </>
  )
}
